import { Maalepunkt } from "codegen";

import { SikringMaalepunkt } from "pages/Sikring/types";
import { isValidMaalepunktId } from "utils/Maalepunkt";

/** Om et målepunkt kan være med i sikringen. Målepunkt lagt til for hånd slås opp for å sjekke prisområdet. */
export enum MeteringPointCheck {
	Ok = "ok",
	Loading = "loading",
	NotFound = "notFound",
	WrongPriceArea = "wrongPriceArea",
	Failed = "failed",
}

/** Et målepunkt i listen for sikring på kundenivå. */
export interface CustomerMeteringPointRow extends SikringMaalepunkt {
	addedManually: boolean;
	check: MeteringPointCheck;
	/** Prisområdet oppslaget fant. Bare satt for målepunkt lagt til for hånd. */
	prisomraade: string | null;
	/** Brukerens valg i sjekkboksen. */
	checked: boolean;
	/** Med i sikringen: valgt av brukeren og sjekket OK. */
	selected: boolean;
}

/** Resultatet av oppslaget på ett målepunkt lagt til for hånd (GET /maalepunkter?maalepunktId=). */
export interface MeteringPointLookup {
	isPending: boolean;
	isError: boolean;
	data?: Maalepunkt[];
}

/**
 * Om brukeren kan velge målepunktet. Målepunkt som ikke finnes eller ligger i et annet prisområde kan aldri være med.
 * Mens oppslaget pågår eller etter at det feilet, kan brukeren fjerne haken, så et oppslag som henger ikke sperrer steget.
 */
export function isSelectable(row: CustomerMeteringPointRow): boolean {
	return row.check !== MeteringPointCheck.NotFound && row.check !== MeteringPointCheck.WrongPriceArea;
}

/**
 * Kundens eksisterende målepunkt først, deretter de som er lagt til for hånd. Alle er inkludert, unntatt de brukeren
 * har ekskludert. En manuelt lagt til ID som også er et eksisterende målepunkt vises bare én gang. Et manuelt lagt
 * til målepunkt er bare med når oppslaget har funnet det i prisområdet sikringen gjelder.
 */
export function buildCustomerMeteringPointList(
	existing: SikringMaalepunkt[],
	addedMeteringPointIds: string[],
	excludedMeteringPointIds: string[],
	lookups: Map<string, MeteringPointLookup>,
	prisomraade: string
): CustomerMeteringPointRow[] {
	const excluded = new Set(excludedMeteringPointIds);
	const existingIds = new Set(existing.map((mp) => mp.maalepunktId));
	const existingRows = existing.map((mp) => toRow(mp, false, MeteringPointCheck.Ok, null, excluded));
	const addedRows = addedMeteringPointIds.filter((id) => !existingIds.has(id)).map((id) => toAddedRow(id, lookups.get(id), prisomraade, excluded));
	return [...existingRows, ...addedRows];
}

function toRow(
	mp: SikringMaalepunkt,
	addedManually: boolean,
	check: MeteringPointCheck,
	foundArea: string | null,
	excluded: Set<string>
): CustomerMeteringPointRow {
	const checked = !excluded.has(mp.maalepunktId);
	return { ...mp, addedManually, check, prisomraade: foundArea, checked, selected: checked && check === MeteringPointCheck.Ok };
}

/** Et målepunkt lagt til for hånd, med opplysningene oppslaget fant. */
function toAddedRow(maalepunktId: string, lookup: MeteringPointLookup | undefined, prisomraade: string, excluded: Set<string>): CustomerMeteringPointRow {
	const found = lookup?.data?.find((mp) => mp.maalepunktId === maalepunktId);
	const mp: SikringMaalepunkt = {
		maalepunktId,
		maalernummer: found?.maalernummer ?? null,
		adresse: found?.adresse ?? null,
		forventetAarsforbrukKwh: found?.forventetAarsforbrukKwh ?? null,
		harSikringsprodukt: null,
	};
	return toRow(mp, true, getCheck(lookup, found, prisomraade), found?.prisomraade ?? null, excluded);
}

function getCheck(lookup: MeteringPointLookup | undefined, found: Maalepunkt | undefined, prisomraade: string): MeteringPointCheck {
	if (lookup?.isError) return MeteringPointCheck.Failed;
	if (!lookup || lookup.isPending) return MeteringPointCheck.Loading;
	if (!found) return MeteringPointCheck.NotFound;
	return found.prisomraade === prisomraade ? MeteringPointCheck.Ok : MeteringPointCheck.WrongPriceArea;
}

export interface ParsedMaalepunktIds {
	/** Nye, gyldige ID-er i rekkefølgen de ble limt inn. */
	newIds: string[];
	/** Antall ID-er som allerede var i listen, eller som ble limt inn flere ganger. */
	duplicateCount: number;
	/** Verdier som ikke er en målepunkt-ID, f.eks. en overskrift fra Excel. */
	invalid: string[];
}

/**
 * Leser målepunkt-ID-er fra innlimt tekst. En kolonne fra Excel kommer som én verdi per linje, men komma, semikolon,
 * tabulator og mellomrom godtas også som skille. ID-er som allerede er i listen filtreres bort.
 */
export function parseMaalepunktIds(text: string, idsInList: string[]): ParsedMaalepunktIds {
	const seen = new Set(idsInList);
	const result: ParsedMaalepunktIds = { newIds: [], duplicateCount: 0, invalid: [] };
	for (const value of text.split(/[\s,;]+/).map((v) => v.replaceAll('"', "").trim())) {
		if (!value) continue;
		if (!isValidMaalepunktId(value)) {
			result.invalid.push(value);
		} else if (seen.has(value)) {
			result.duplicateCount++;
		} else {
			seen.add(value);
			result.newIds.push(value);
		}
	}
	return result;
}
